import { useEffect, useState } from "react";
import type { GeoJsonImportPlan } from "shared";
import { ACCENT_HEX, planGeoJsonImport } from "shared";
import { mapsApi } from "@/api/maps";
import { useToast } from "@/components/feedback/Toast";
import { Button } from "@/components/primitives/Button";
import { Dialog, DialogActions } from "@/components/primitives/Dialog";

type ImportPlan = Extract<GeoJsonImportPlan, { ok: true }>;

type Props = {
	open: boolean;
	mapId: string;
	onClose: () => void;
	onImported: () => Promise<void> | void;
};

const MAX_FILE_BYTES = 15 * 1024 * 1024;

export function ImportDialog({ open, mapId, onClose, onImported }: Props) {
	const [fileName, setFileName] = useState<string | null>(null);
	const [raw, setRaw] = useState<unknown>(null);
	const [plan, setPlan] = useState<ImportPlan | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [importing, setImporting] = useState(false);
	const { toast } = useToast();

	useEffect(() => {
		if (!open) return;
		setFileName(null);
		setRaw(null);
		setPlan(null);
		setError(null);
		setImporting(false);
	}, [open]);

	const handleFile = async (file: File | undefined | null) => {
		if (!file) return;
		setError(null);
		setPlan(null);
		setRaw(null);
		setFileName(file.name);
		if (file.size > MAX_FILE_BYTES) {
			setError("Datei ist zu groß (max. 15 MB)");
			return;
		}
		try {
			const parsed: unknown = JSON.parse(await file.text());
			const result = planGeoJsonImport(parsed);
			if (!result.ok) {
				setError(
					result.index !== undefined
						? `Feature ${result.index + 1}: ${result.message}`
						: result.message,
				);
				return;
			}
			setRaw(parsed);
			setPlan(result);
		} catch {
			setError("Datei enthält kein gültiges JSON");
		}
	};

	const handleImport = async () => {
		if (!plan || raw === null || importing) return;
		setImporting(true);
		setError(null);
		try {
			const res = await mapsApi.importGeojson(mapId, raw);
			toast(`${res.entriesCreated} Einträge importiert`, "success");
			onClose();
			await onImported();
		} catch (e) {
			setError(e instanceof Error ? e.message : "Import fehlgeschlagen");
		} finally {
			setImporting(false);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={importing ? () => {} : onClose}
			title="GeoJSON importieren"
			description="Legt neue Ebenen und Einträge aus einer GeoJSON-Datei an."
		>
			<div className="mt-4 flex flex-col gap-3">
				<label className="flex flex-col gap-1.5 text-sm">
					<span className="text-[13px] font-medium text-fg">
						Datei{fileName ? `: ${fileName}` : ""}
					</span>
					<input
						type="file"
						accept=".geojson,.json,application/geo+json,application/json"
						disabled={importing}
						onChange={(e) => {
							void handleFile(e.target.files?.[0]);
							e.target.value = "";
						}}
						className="h-9 w-full rounded-[var(--radius-control)] bg-surface px-3 text-sm text-fg ring-1 ring-[var(--ring-hairline)] outline-none focus:ring-2 focus:ring-teal-500 file:mr-3 file:h-7 file:cursor-pointer file:rounded-[var(--radius-control)] file:border-0 file:bg-teal-500/10 file:px-3 file:text-sm file:font-medium file:text-teal-600 dark:file:text-teal-400"
					/>
				</label>

				{plan ? (
					<div className="rounded-[var(--radius-control)] p-3 ring-1 ring-[var(--ring-hairline)]">
						<p className="text-sm font-medium text-fg">
							{plan.featureCount} Eintrag{plan.featureCount === 1 ? "" : "e"} ·{" "}
							{plan.groups.length} {plan.groups.length === 1 ? "Ebene" : "Ebenen"}
						</p>
						<ul className="mt-2 space-y-1.5">
							{plan.groups.map((group) => (
								<li
									key={group.key}
									className="flex items-center gap-2 text-sm text-fg-muted"
								>
									<span
										className="h-2.5 w-2.5 shrink-0 rounded-full"
										style={{ backgroundColor: ACCENT_HEX[group.accent] }}
									/>
									<span className="min-w-0 flex-1 truncate">{group.layerTitle}</span>
									<span>{group.entries.length}</span>
								</li>
							))}
						</ul>
					</div>
				) : null}

				{error ? (
					<p className="text-xs text-rose-600 dark:text-rose-400">{error}</p>
				) : null}
			</div>
			<DialogActions>
				<Button
					type="button"
					variant="secondary"
					size="sm"
					onClick={onClose}
					disabled={importing}
				>
					Abbrechen
				</Button>
				<Button
					type="button"
					size="sm"
					disabled={!plan || importing}
					onClick={() => void handleImport()}
				>
					{importing ? "Importiere…" : "Importieren"}
				</Button>
			</DialogActions>
		</Dialog>
	);
}
